<template>
  <v-card
    v-if="item"
    active-class="white"
    class="content-card px-4 py-3 d-flex flex-column justify-space-between"
    data-tourid="boards-column-content-cards"
    outlined
    :width="width"
    :ripple="false"
    @click="handleClick"
  >
    <div>
      <div class="d-flex">
        <div class="icon-container">
          <v-icon
            v-if="entityType === 'task'"
            size="20"
            :color="isCompleted ? 'green' : 'blue-grey darken-1'"
            @click.stop="handleTaskStatusClick"
          >
            {{ statusTaskIcon }}
          </v-icon>
          <EntityIcon v-else size="20" :entityType="entityType" class="me-1" />
        </div>
        <div class="ms-2 text-left flex-grow-1">
          <p
            v-snip="1"
            class="pa-0 ma-0 blue-grey--text text--darken-3 font-size-10 font-weight-500 entity-name"
          >
            {{ entityName }}
          </p>
          <p
            class="creation-date pa-0 ma-0 blue-grey--text text--darken-1 mt-1"
          >
            {{ $t('common.created') }}:
            {{ creationDate }}
          </p>
        </div>
        <div class="d-flex flex-shrink-0">
          <v-tooltip v-if="shouldShowHelper" bottom max-width="250px">
            <template v-slot:activator="{ on }">
              <v-icon class="me-2" color="yellow" size="16px" v-on="on"
                >mdi-information-outline
              </v-icon>
            </template>
            <p class="body2 mb-0">
              {{ $t('common.users.result_based_on_matches_by_fields') }}
            </p>
            <ul>
              <li v-for="label in matchingFieldLabels" :key="label">
                {{ label }}
              </li>
            </ul>
          </v-tooltip>
          <StatusChip showValidationIcons :entity="item" />
        </div>
      </div>
      <text-highlight
        highlightClass="yellow lighten-1"
        class="pa-0 ma-0 mt-3 name-container blue-grey--text text--darken-3 text-left flex-shrink-0"
        highlightComponent="span"
        :queries="highlightQueries"
        :title="name"
      >
        {{ name }}
      </text-highlight>
      <div v-if="item.linkedEntityId" class="mt-2 mb-1">
        <v-chip
          small
          color="blue-grey lighten-3"
          class="linked-entity px-2"
          @click.stop="navigateTo(linkedEntityRoute)"
        >
          <EntityIcon
            size="10"
            :entityType="item.linkedEntityType"
            class="me-1"
          />
          <p
            class="ma-0 pa-0 blue--text text-truncate linked-entity-name font-size-8"
          >
            {{ linkedEntityText }}
          </p>
        </v-chip>
      </div>
      <div class="flex-grow-1" v-if="tags.length">
        <ChipList class="content-card-chip-tags mt-1" :items="tags" />
      </div>
    </div>
    <div class="d-flex align-center mt-1">
      <div class="flex-grow-0 d-flex flex-column">
        <p
          v-if="dueDate && !isCompleted"
          class="ma-0 pa-0 font-size-10 d-flex flex-grow-1 align-center due-date-container"
          :class="isOverdue ? 'red--text' : 'blue-grey--text text--darken-1'"
        >
          {{ $t('common.due') }}:
          {{ dueDateText }}
          <v-icon v-if="isOverdue" class="ml-1" size="16" color="red"
            >mdi-alert-circle-outline</v-icon
          >
        </p>
        <p
          v-if="isCompleted && source.completedAt"
          class="ma-0 pa-0 font-size-10 d-flex flex-grow-1 align-center due-date-container blue-grey--text text--darken-1"
        >
          {{ completeDateText }}
        </p>
      </div>

      <div class="flex-shrink-1 flex-grow-1 d-flex align-center justify-end ">
        <Priority v-if="priority" class="mr-3" small :priority="priority" />
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <span v-on="on">
              <UserAvatar size="25px" font-size="10px" :account="createdBy" />
            </span>
          </template>
          <span>{{
            $t('common.created_by_user', { userName: getFullName(createdBy) })
          }}</span>
        </v-tooltip>
      </div>
    </div>
    <EntityDrawer
      v-if="showContentDrawer"
      class="cursor-default"
      :content="item"
      :visible="showContentDrawer"
      @deleted="localUpdateItem(null)"
      @edited="localUpdateItem($event)"
      @close="showContentDrawer = false"
      @navigate="trackBoardsActions('boards.Navigate', { section: entityType })"
      @clickStatus="
        trackBoardsActions('boards.ClickOnStatusDrawer', {
          entityType,
          id: item.id,
        })
      "
    />
  </v-card>
</template>

<script>
import moment from 'moment'
import _ from 'lodash'
import { mapGetters } from 'vuex'
import api from '@/api'
import { utils } from 'frontend-js-shared'

import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import revMixin from '@/mixins/revMixin'
import mixin from '@/mixins/mixin'
import userMixin from '@/mixins/userMixin.vue'

import EntityIcon from '@/components/EntityIcon.vue'
import StatusChip from '@/components/Core/StatusChip.vue'
import UserAvatar from '@/components/UserAvatar.vue'
import ChipList from '@/components/ChipList.vue'
import EntityDrawer from '@/components/EntityDrawer/EntityDrawer.vue'
import Priority from '@/components/Priority.vue'

import eventHub, { HUB_EVENTS } from '@/lib/event-hub'

export default {
  name: 'ContentCard',

  mixins: [trackerMixin, revMixin, mixin, userMixin],

  components: {
    StatusChip,
    UserAvatar,
    ChipList,
    EntityDrawer,
    Priority,
    EntityIcon,
  },

  props: {
    source: { type: Object, required: true },
    terms: { type: String, default: '' },
    width: { type: String, default: '328px' },
  },

  data() {
    return {
      item: {},
      showContentDrawer: false,
    }
  },

  watch: {
    source: {
      immediate: true,
      handler(val) {
        this.item = val
      },
    },
  },

  computed: {
    ...mapGetters(['organization', 'organizationId']),

    completeDateText() {
      if (!this.source?.completedAt) return null
      return `${this.$t('common.completed')}: ${utils.dates.humanDate(
        this.source.completedAt
      )}`
    },

    isCompleted() {
      return this.item?.status === this.$constants.tasksStatus.completed
    },

    statusTaskIcon() {
      return this.isCompleted ? 'mdi-check-circle' : 'mdi-radiobox-blank'
    },

    linkedEntityText() {
      const customName =
        this.organization?.customNamings?.[this.item.linkedEntityType]
          ?.singular ?? null

      const entityName = this.$t(
        `common.${_.snakeCase(this.item.linkedEntityType)}_one`
      )

      return this.$t('tasks.link_to_entity', {
        entity: customName || entityName,
      })
    },

    linkedEntityRoute() {
      return this.generateRevRoute(
        this.organizationId,
        this.item.linkedEntityType,
        this.item.linkedEntityId
      )
    },

    createdBy() {
      return this.item.createdBy
    },

    createdAt() {
      return this.item.createdAt
    },

    priority() {
      return this.item.priority
    },

    dueDate() {
      return this.item.dueDate
    },

    name() {
      return this.item.name
    },

    creationDate() {
      return utils.dates.formatDate(this.createdAt, 'MMM D, YY')
    },

    dueDateText() {
      return utils.dates.humanDueDate(this.dueDate) ?? ''
    },

    entityType() {
      return this.item.entityType
    },

    findingId() {
      if (this.entityType !== 'finding') return null
      return this.item.id
    },

    entityName() {
      switch (this.entityType) {
        case 'routine':
          return this.$t('routines.routine_one')
        case 'finding':
          return this.$t('routines.finding_one')
        case 'challenge':
          return this.$t('common.challenge_one')
        case 'task':
          return this.$t('common.task_one')
        default:
          return (
            this.organization?.customNamings?.[this.entityType]?.singular ||
            this.$t(`common.rev_type.${_.snakeCase(this.entityType)}_one`) ||
            ''
          )
      }
    },

    highlightQueries() {
      // if this.terms is undefined, the spread operator will be used on an empty array instead of undefined.
      // if we don't do this, using ...undefined will throw an error
      return [this.terms, ...(this.terms?.split(' ') || [])]
    },

    tags() {
      return (
        this.item?.tags?.map((tag, index) => {
          if (tag?.name?.length >= 25 && index === 0)
            return `${tag?.name.slice(0, 25)}...`
          return tag?.name
        }) ?? []
      )
    },

    isOverdue() {
      const today = moment()
        .add(0, 'days')
        .format('YYYY-MM-DD')
      const dueDate = moment(this.dueDate).format('YYYY-MM-DD')

      return dueDate < today
    },

    matchingFieldLabels() {
      return this.item?.matchingFields?.map?.(field =>
        this.$t(`explore.fields.${field.replace('.', '_')}`)
      )
    },

    shouldShowHelper() {
      if (!this.item?.matchingFields?.length) return false
      return !this.item?.matchingFields?.some?.(field =>
        ['name', 'situation'].includes(field)
      )
    },
  },

  methods: {
    cleanupTaskDrawerListeners() {
      eventHub.$off(HUB_EVENTS.TASK_EDITION_DRAWER.UPDATED, this.localUpdateItem)
      eventHub.$off(HUB_EVENTS.TASK_EDITION_DRAWER.DELETED, this.localUpdateItem)
    },

    async handleTaskStatusClick() {
      const currentStatus = this.item?.status
      try {
        const newTaskStatus =
          this.item?.status !== this.$constants.tasksStatus.completed
            ? this.$constants.tasksStatus.completed
            : this.$constants.tasksStatus.open
        this.localUpdateItem({
          ...this.item,
          status: newTaskStatus,
        })
        await api.tasks.update(this.organizationId, this.item.id, {
          status: newTaskStatus,
        })
        this.trackTasks('tasks.complete', {
          taskId: this.item.id,
          previousStatus: currentStatus,
          status: newTaskStatus,
          section: this.$route.name,
        })
        this.showSuccess(this.$t('tasks.updated_successfully'))
        eventHub.$emit(HUB_EVENTS.TASK_EDITION_DRAWER.COMPLETED)
      } catch (err) {
        this.localUpdateItem({
          ...this.item,
          status: currentStatus,
        })
        this.handleRequestError(err)
      }
    },

    openTaskDrawer(taskId) {
      this.cleanupTaskDrawerListeners()
      eventHub.$emit(HUB_EVENTS.TASK_EDITION_DRAWER.UPDATE, taskId)
      eventHub.$on(HUB_EVENTS.TASK_EDITION_DRAWER.UPDATED, this.localUpdateItem)
      eventHub.$on(HUB_EVENTS.TASK_EDITION_DRAWER.DELETED, this.localUpdateItem)
    },

    localUpdateItem(item) {
      this.item = item ? this.getItem(item) : null
      eventHub.$emit(HUB_EVENTS.BOARD_VIEW.UPDATED)
      this.cleanupTaskDrawerListeners()
    },

    getItem(item) {
      switch (this.source.entityType) {
        case 'finding':
          return {
            ...this.source,
            ...item,
            createdBy: this.source.createdBy,
            name: item.situation,
          }
        case 'task':
          return {
            ...this.source,
            ...item,
            entityType: 'task',
            linkedEntityType: item.linkedEntityType ?? item.entityType,
            linkedEntityId: item.linkedEntityId ?? item.entityId,
          }
        default:
          return { ...this.source, ...item }
      }
    },

    handleClick() {
      //This if is needed because this card cannot emit events to colum board components due to the virtual list
      this.$emit('click')
      if (this.$route.name === 'boards') {
        this.trackBoardsActions('boards.ClickOnCard', {
          entityType: this.entityType,
          id: this.item.id,
        })
      }

      if (this.entityType === 'task') {
        this.trackHomeNavigate({ section: 'toDo', element: 'task' })
        return this.openTaskDrawer(this.item.id)
      }

      this.showContentDrawer = true
    },
  },

  beforeDestroy() {
    this.cleanupTaskDrawerListeners()
  },
}
</script>

<style scoped lang="scss">
.entity-name {
  line-height: 16px;
}

.content-card {
  .icon-container {
    width: 20px !important;
    height: 20px !important;
  }
  .creation-date {
    font-size: $font-size-10 !important;
    font-weight: 400;
    line-height: 8px;
  }
  .name-container {
    font-size: $font-size-12 !important;
    font-weight: 600;
    line-height: 16px;
    letter-spacing: 0.4px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  .due-date-container {
    line-height: 8px;
  }
}
</style>
<style lang="scss">
.content-card-chip-tags {
  .v-chip.v-size--small {
    height: 16px !important;
    span {
      font-size: $font-size-8 !important;
      line-height: 8px !important;
    }
  }
}
.linked-entity-name:hover {
  text-decoration: underline $color-blue;
}
.card-hover:hover {
  .linked-entity {
    background-color: $color-blue-grey-lighten2 !important;
  }
}
</style>
